import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Music2, Volume2, VolumeX, Pause, Play, SkipBack, SkipForward } from 'lucide-react';

// Playlists by application
const PLAYLISTS = {
  // Default / REVELIAE main
  default: [
    { name: 'Tarot Ambiance', src: '/audio/tarot.mp3' }
  ],
  // MYSTERIAE - Tarot
  mysteriae: [
    { name: 'Tarot', src: '/audio/tarot.mp3' },
    { name: 'Tarot Life', src: '/audio/tarot_life.mp3' },
    { name: 'Tarot Obscur', src: '/audio/tarot_obscur.mp3' }
  ],
  // NUMEROLOGIA - Numerology
  numerologia: [
    { name: 'Numerologia', src: '/audio/numerologia_main.mp3' },
    { name: 'Ancient', src: '/audio/numerologia_ancient.mp3' },
    { name: 'Quantic', src: '/audio/numerologia_quantic.mp3' }
  ],
  // LUMINA - Palmistry
  lumina: [
    { name: 'Lumina 01', src: '/audio/lumina_01.mp3' },
    { name: 'Lumina 02', src: '/audio/lumina_02.mp3' },
    { name: 'Solare 01', src: '/audio/lumina_solare_01.mp3' },
    { name: 'Solare 02', src: '/audio/lumina_solare_02.mp3' }
  ],
  // ASTROZODIAC - Astrology
  astrozodiac: [
    { name: 'Astrozodiac', src: '/audio/astrozodiac_main.mp3' },
    { name: 'Solar Zodiac', src: '/audio/astrozodiac_solar_zodiac.mp3' },
    { name: 'Deep Galaxy', src: '/audio/astrozodiac_deep_galaxy.mp3' }
  ]
};

// Playlist toggle component for all REVELIAE apps
const PlaylistToggle = ({ theme = 'amber', app = 'default' }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [currentApp, setCurrentApp] = useState(app);
  const audioRef = useRef(null);
  
  // Get playlist for current app - use state to ensure re-render
  const playlist = PLAYLISTS[currentApp] || PLAYLISTS.default;
  const hasMultipleTracks = playlist.length > 1;

  // Update currentApp when prop changes
  useEffect(() => {
    if (app !== currentApp) {
      setCurrentApp(app);
      setCurrentTrack(0); // Reset to first track when app changes
    }
  }, [app, currentApp]);

  // Theme colors
  const themes = {
    amber: {
      bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
      bgOff: 'bg-gray-300',
      text: 'text-amber-600',
      border: 'border-amber-200',
      hover: 'hover:bg-amber-50',
      accent: 'accent-amber-500'
    },
    purple: {
      bg: 'bg-gradient-to-r from-purple-500 to-indigo-500',
      bgOff: 'bg-gray-300',
      text: 'text-purple-600',
      border: 'border-purple-200',
      hover: 'hover:bg-purple-50',
      accent: 'accent-purple-500'
    },
    sky: {
      bg: 'bg-gradient-to-r from-sky-500 to-cyan-500',
      bgOff: 'bg-gray-300',
      text: 'text-sky-600',
      border: 'border-sky-200',
      hover: 'hover:bg-sky-50',
      accent: 'accent-sky-500'
    },
    emerald: {
      bg: 'bg-gradient-to-r from-emerald-500 to-teal-500',
      bgOff: 'bg-gray-300',
      text: 'text-emerald-600',
      border: 'border-emerald-200',
      hover: 'hover:bg-emerald-50',
      accent: 'accent-emerald-500'
    },
    indigo: {
      bg: 'bg-gradient-to-r from-indigo-500 to-violet-500',
      bgOff: 'bg-gray-300',
      text: 'text-indigo-600',
      border: 'border-indigo-200',
      hover: 'hover:bg-indigo-50',
      accent: 'accent-indigo-500'
    }
  };

  const currentTheme = themes[theme] || themes.amber;

  // Initialize audio element
  useEffect(() => {
    // Clean up previous audio if exists
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    const currentPlaylist = PLAYLISTS[currentApp] || PLAYLISTS.default;
    audioRef.current = new Audio(currentPlaylist[0].src);
    audioRef.current.volume = volume;
    
    // When track ends, go to next track or loop
    const handleEnded = () => {
      const pl = PLAYLISTS[currentApp] || PLAYLISTS.default;
      if (pl.length > 1) {
        setCurrentTrack((prev) => (prev + 1) % pl.length);
      } else {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.log('Auto-play prevented:', e));
      }
    };
    
    audioRef.current.addEventListener('ended', handleEnded);

    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', handleEnded);
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [currentApp]);

  // Handle track changes
  useEffect(() => {
    const currentPlaylist = PLAYLISTS[currentApp] || PLAYLISTS.default;
    if (audioRef.current && currentPlaylist[currentTrack]) {
      const wasPlaying = isPlaying;
      audioRef.current.src = currentPlaylist[currentTrack].src;
      audioRef.current.volume = volume;
      if (wasPlaying) {
        audioRef.current.play().catch(e => console.log('Auto-play prevented:', e));
      }
    }
  }, [currentTrack, currentApp]);

  // Persist state in localStorage
  useEffect(() => {
    const saved = localStorage.getItem('reveliae_playlist_enabled');
    const savedVolume = localStorage.getItem('reveliae_playlist_volume');
    const savedTrack = localStorage.getItem(`reveliae_playlist_track_${currentApp}`);
    
    if (saved !== null) {
      setIsEnabled(saved === 'true');
    }
    if (savedVolume !== null) {
      setVolume(parseFloat(savedVolume));
    }
    if (savedTrack !== null) {
      const currentPlaylist = PLAYLISTS[currentApp] || PLAYLISTS.default;
      const trackIndex = parseInt(savedTrack);
      if (trackIndex < currentPlaylist.length) {
        setCurrentTrack(trackIndex);
      }
    }
  }, [currentApp]);

  useEffect(() => {
    localStorage.setItem('reveliae_playlist_enabled', isEnabled.toString());
    if (!isEnabled) {
      setIsPlaying(false);
      if (audioRef.current) {
        audioRef.current.pause();
      }
    }
  }, [isEnabled]);

  // Handle play/pause
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(e => console.log('Playback prevented:', e));
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
    localStorage.setItem('reveliae_playlist_volume', volume.toString());
  }, [volume]);

  // Save current track for this app
  useEffect(() => {
    localStorage.setItem(`reveliae_playlist_track_${currentApp}`, currentTrack.toString());
  }, [currentTrack, currentApp]);

  const togglePlaylist = () => {
    setIsEnabled(!isEnabled);
  };

  const togglePlayPause = () => {
    if (isEnabled) {
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeChange = (e) => {
    setVolume(parseFloat(e.target.value));
  };

  const nextTrack = () => {
    if (hasMultipleTracks) {
      setCurrentTrack((prev) => (prev + 1) % playlist.length);
    }
  };

  const prevTrack = () => {
    if (hasMultipleTracks) {
      setCurrentTrack((prev) => (prev - 1 + playlist.length) % playlist.length);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed bottom-20 right-6 z-50"
      data-testid="playlist-toggle"
    >
      <div className={`
        flex items-center gap-3 px-4 py-3 
        bg-white/95 backdrop-blur-md rounded-full 
        shadow-lg border ${currentTheme.border}
        transition-all duration-300
      `}>
        {/* Status indicator */}
        <AnimatePresence>
          {isEnabled && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              className="flex items-center gap-2 overflow-hidden"
            >
              {/* Previous track button (only if multiple tracks) */}
              {hasMultipleTracks && (
                <button
                  onClick={prevTrack}
                  className={`p-1 rounded transition-all ${currentTheme.hover} ${currentTheme.text}`}
                  data-testid="prev-track-btn"
                >
                  <SkipBack className="h-3 w-3" />
                </button>
              )}

              {/* Play/Pause button */}
              <button
                onClick={togglePlayPause}
                className={`
                  p-2 rounded-full transition-all
                  ${currentTheme.hover} ${currentTheme.text}
                `}
                data-testid="play-pause-btn"
              >
                {isPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </button>

              {/* Next track button (only if multiple tracks) */}
              {hasMultipleTracks && (
                <button
                  onClick={nextTrack}
                  className={`p-1 rounded transition-all ${currentTheme.hover} ${currentTheme.text}`}
                  data-testid="next-track-btn"
                >
                  <SkipForward className="h-3 w-3" />
                </button>
              )}

              {/* Music visualizer */}
              {isPlaying && (
                <div className="flex items-end gap-0.5 h-4">
                  {[1, 2, 3, 4].map((bar) => (
                    <motion.div
                      key={bar}
                      className={`w-1 ${currentTheme.bg} rounded-full`}
                      animate={{
                        height: ['4px', '16px', '8px', '12px', '4px'],
                      }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        delay: bar * 0.1,
                        ease: 'easeInOut'
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Track name and counter */}
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${currentTheme.text} whitespace-nowrap max-w-[100px] truncate`}>
                  {playlist[currentTrack].name}
                </span>
                {hasMultipleTracks && (
                  <span className="text-xs text-gray-400">
                    {currentTrack + 1}/{playlist.length}
                  </span>
                )}
              </div>

              {/* Volume control */}
              <div className="flex items-center gap-1">
                <button 
                  onClick={() => setVolume(volume > 0 ? 0 : 0.5)}
                  className={`p-1 rounded ${currentTheme.hover}`}
                  data-testid="volume-toggle-btn"
                >
                  {volume === 0 ? (
                    <VolumeX className={`h-3 w-3 ${currentTheme.text}`} />
                  ) : (
                    <Volume2 className={`h-3 w-3 ${currentTheme.text}`} />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={handleVolumeChange}
                  className={`w-16 h-1 ${currentTheme.accent} cursor-pointer`}
                  data-testid="volume-slider"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main toggle */}
        <div className="flex items-center gap-2">
          <Music2 className={`h-4 w-4 ${isEnabled ? currentTheme.text : 'text-gray-400'}`} />
          
          {/* Toggle switch */}
          <button
            onClick={togglePlaylist}
            className={`
              relative w-12 h-6 rounded-full transition-all duration-300
              ${isEnabled ? currentTheme.bg : currentTheme.bgOff}
            `}
            data-testid="playlist-toggle-btn"
          >
            <motion.div
              className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
              animate={{
                left: isEnabled ? '28px' : '4px'
              }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          </button>
        </div>
      </div>

      {/* Tooltip on first visit */}
      {!localStorage.getItem('reveliae_playlist_seen') && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0 }}
          className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap"
          onAnimationComplete={() => {
            setTimeout(() => {
              localStorage.setItem('reveliae_playlist_seen', 'true');
            }, 3000);
          }}
        >
          Activez la playlist d'ambiance
          <div className="absolute bottom-0 right-6 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" />
        </motion.div>
      )}
    </motion.div>
  );
};

export default PlaylistToggle;
